import React from "react";
import ContactCard from "../../../components/general/contactCard";
import {
  publicLegalShellStyle,
  publicMutedTextStyle,
  publicPageContainerStyle,
  publicSectionHeadingStyle,
  renderPublicLegalHeader,
} from "../../../components/general/publicPageStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const sectionHeadingStyle: React.CSSProperties = publicSectionHeadingStyle;

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "1rem",
  marginBottom: "1.5rem",
  fontSize: "0.95rem",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  backgroundColor: "#f4f8fc",
  border: "1px solid #d9e8f5",
  color: "#0f3d63",
  padding: "0.8rem",
  verticalAlign: "top",
};

const tdStyle: React.CSSProperties = {
  border: "1px solid #d9e8f5",
  padding: "0.8rem",
  verticalAlign: "top",
  color: "#243447",
};

export default function HipaaCompliance() {
  const authExperience = useAuthExperience();
  const platformDomain = authExperience.publicSite.platformDomain;
  const brandName = authExperience.brandName;
  const companyName = authExperience.company.companyName;
  const operationsLabel = authExperience.vocabulary.operationsLabel;
  const facilityPlural = authExperience.vocabulary.facilityPlural;

  return (
    <div style={publicPageContainerStyle}>
      <div className="prose" style={{ ...publicLegalShellStyle, fontFamily: "Georgia, 'Times New Roman', serif" }}>
        {renderPublicLegalHeader(
          `HIPAA and Security at ${brandName}`,
          <><strong>{platformDomain}</strong> &mdash; A Service of {companyName}</>,
          <><strong>Last Updated:</strong> June 23, 2026</>,
        )}

        <p style={{ ...publicMutedTextStyle, marginBottom: "1.5rem" }}>
          {brandName} is designed to support {operationsLabel} that involve protected health information
          ("PHI"), including resident records, care plans, medication workflows, incident reports, daily notes,
          document storage, and certain AI-assisted drafting workflows. This page explains, in plain language,
          what we do to support HIPAA-aligned operations and what responsibilities remain with the customer using
          the platform.
        </p>

        <h3 style={sectionHeadingStyle}>What {brandName} Does</h3>
        <p>
          {brandName} provides a cloud-based software platform for resident documentation, care-plan management,
          medication workflows, document storage, reminders, binder records, resident assessment support, and
          related operational tasks for {facilityPlural}. Because these workflows can involve PHI, we structure
          the platform around security, minimum-necessary access, auditability, and business-associate handling.
        </p>

        <h3 style={sectionHeadingStyle}>Important HIPAA Note</h3>
        <p>
          No software product becomes "HIPAA certified" by itself. HIPAA compliance depends on both:
        </p>
        <ul>
          <li>the safeguards we implement as the software provider; and</li>
          <li>the way each customer uses the product in day-to-day operations.</li>
        </ul>
        <p>
          {brandName} is built to support HIPAA-compliant use, and we make a Business Associate Agreement available.
          Customers remain responsible for their own workforce training, internal policies, access oversight,
          documentation practices, and legal obligations as Covered Entities or Business Associates.
        </p>

        <h3 style={sectionHeadingStyle}>Key Safeguards</h3>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Area</th>
              <th style={thStyle}>What {brandName} Does</th>
              <th style={thStyle}>Why It Matters</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}><strong>Authentication</strong></td>
              <td style={tdStyle}>
                Accounts use password-based sign-in plus a one-time verification code sent by email. MFA is enforced
                for provider, nurse, caregiver, family-member, and admin access.
              </td>
              <td style={tdStyle}>
                Adds a second factor beyond the password and helps reduce the risk of unauthorized access.
              </td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Role-based access</strong></td>
              <td style={tdStyle}>
                Platform access is segmented by role and relationship, including provider, nurse, caregiver,
                family-member, and admin workflows.
              </td>
              <td style={tdStyle}>
                Helps limit PHI access to the users and workflows that need it.
              </td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Encryption in transit</strong></td>
              <td style={tdStyle}>
                Platform traffic is delivered over HTTPS/TLS to protect information moving between the user and the
                system.
              </td>
              <td style={tdStyle}>
                Helps protect PHI from interception during transmission.
              </td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Secure cloud infrastructure</strong></td>
              <td style={tdStyle}>
                {brandName} uses AWS services including Cognito, Lambda, S3, DynamoDB, SES, CloudWatch, Textract, and
                Bedrock in support of platform operations and document workflows.
              </td>
              <td style={tdStyle}>
                Keeps infrastructure centralized in a managed cloud environment with documented controls and service
                boundaries.
              </td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>AWS BAA coverage</strong></td>
              <td style={tdStyle}>
                {brandName} maintains an AWS Business Associate Addendum and tracks HIPAA-relevant AWS service usage in
                internal compliance records.
              </td>
              <td style={tdStyle}>
                Supports the subcontractor and vendor side of HIPAA handling for AWS-hosted infrastructure.
              </td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Audit logging</strong></td>
              <td style={tdStyle}>
                {brandName} records platform audit events for important actions such as authentication-related workflows,
                document operations, automation jobs, resident updates, and medication/eMAR write actions.
              </td>
              <td style={tdStyle}>
                Supports accountability, investigation, and operational review.
              </td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Document controls</strong></td>
              <td style={tdStyle}>
                Resident and home documents are handled through platform storage workflows rather than public file
                links. Binder and document-management tools are designed for authenticated use.
              </td>
              <td style={tdStyle}>
                Helps keep PHI-containing documents inside controlled product workflows.
              </td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>AI workflow boundaries</strong></td>
              <td style={tdStyle}>
                AI-assisted features are initiated by authorized users for specific tasks such as drafting,
                classification, document comparison, or structured extraction. Output is intended as a workflow aid
                and should be reviewed before operational use.
              </td>
              <td style={tdStyle}>
                Helps keep AI use intentional, reviewable, and tied to specific requested workflows.
              </td>
            </tr>
          </tbody>
        </table>

        <h3 style={sectionHeadingStyle}>How We Handle AI-Assisted Features</h3>
        <p>
          {brandName} includes AI-assisted workflows for document drafting, record comparison, extraction, and review
          support. These workflows may involve resident information when an authorized user chooses to submit
          documents or prompts that contain PHI.
        </p>
        <ul>
          <li>AI workflows are user-initiated. They are not autonomous background clinical decision makers.</li>
          <li>Users remain responsible for reviewing and approving AI-generated output before relying on it.</li>
          <li>We treat AI output as drafting or workflow support, not medical advice.</li>
          <li>We document AWS vendor scope, including Bedrock and Textract, in our HIPAA documentation set.</li>
        </ul>
        <p>
          We strongly recommend that customers apply the minimum-necessary principle when using AI-assisted features
          and verify all generated output against the resident's actual record and orders.
        </p>

        <h3 style={sectionHeadingStyle}>Medication and eMAR Workflows</h3>
        <p>
          {brandName} supports medication-related workflows including medication list management, MAR comparison support,
          and eMAR-related documentation. These workflows can involve sensitive resident information and therefore are
          handled as part of the platform's PHI scope.
        </p>
        <ul>
          <li>Medication changes and key eMAR-related write actions are included in audit coverage.</li>
          <li>MAR comparison tools are designed to help reduce missed dosage, route, frequency, and usage changes.</li>
          <li>Imported or AI-assisted medication data should still be reviewed by authorized staff before use.</li>
        </ul>

        <h3 style={sectionHeadingStyle}>Operational Safeguards We Maintain</h3>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Program Area</th>
              <th style={thStyle}>Current {brandName} Practice</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}><strong>Business Associate handling</strong></td>
              <td style={tdStyle}>Business Associate Agreement available for customers and AWS BAA maintained for infrastructure support.</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Risk documentation</strong></td>
              <td style={tdStyle}>Internal HIPAA risk assessment, policy set, vendor inventory, and recurring update tracker are maintained.</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Access review process</strong></td>
              <td style={tdStyle}>Access review and MFA evidence are tracked through internal HIPAA documentation.</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Incident response</strong></td>
              <td style={tdStyle}>Incident response planning, breach review checklist, and incident register documentation are maintained.</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Vendor review</strong></td>
              <td style={tdStyle}>HIPAA-relevant vendor scope is tracked, including AWS service coverage and minimum-necessary billing vendor use.</td>
            </tr>
          </tbody>
        </table>

        <h3 style={sectionHeadingStyle}>What Customers Still Need To Do</h3>
        <p>
          {brandName} can support HIPAA-aligned operations, but each customer still needs to operate responsibly. That
          includes:
        </p>
        <ul>
          <li>training workforce members on HIPAA and secure system use;</li>
          <li>granting access only to authorized users;</li>
          <li>reviewing AI-generated or imported content before final use;</li>
          <li>keeping local devices, browsers, and email accounts secure;</li>
          <li>using internal policies for incident response, sanctions, and access review; and</li>
          <li>meeting any legal obligations specific to the customer's organization, state, or licensing context.</li>
        </ul>

        <h3 style={sectionHeadingStyle}>What This Page Does Not Mean</h3>
        <ul>
          <li>It does not mean {brandName} provides legal advice, compliance certification, or medical judgment.</li>
          <li>It does not replace the customer's own HIPAA policies, workforce training, or access oversight.</li>
          <li>It does not mean every workflow is safe without human review. Authorized users still need to review critical records and outputs.</li>
        </ul>

        <h3 style={sectionHeadingStyle}>Business Associate Agreement and Questions</h3>
        <p>
          Customers who need a contractual HIPAA framework for use of the platform should review our{" "}
          <strong>Business Associate Agreement</strong>. If you have questions about HIPAA handling, vendor scope, or
          security practices, please contact us.
        </p>
        <ContactCard />
      </div>
    </div>
  );
}
